import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Box, Typography, TextField, Button, Paper, Grid, InputAdornment,
  IconButton, Alert, CircularProgress,
  Divider, Chip,
} from '@mui/material';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import API_BASE_URL from '../../Config/config';

const TSGLogo = ({ size = 44, onDark = false }) => {
  const [imgError, setImgError] = useState(false);
  if (imgError) {
    return (
      <Box sx={{
        width: size, height: size, borderRadius: '12px', flexShrink: 0,
        background: onDark ? 'rgba(255,255,255,0.18)' : 'linear-gradient(135deg,#6C3BFF,#8B5CF6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: size * 0.46 }}>T</Typography>
      </Box>
    );
  }
  if (onDark) {
    return (
      <Box sx={{
        bgcolor: '#FFFFFF', borderRadius: '10px', px: 1, py: '5px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
      }}>
        <Box component="img"
          src="https://skillgenie-college-uploads.s3.ap-south-1.amazonaws.com/TSG_Logo.png"
          alt="The Skill Genie" loading="lazy" onError={() => setImgError(true)}
          sx={{ height: size * 0.72, width: 'auto', objectFit: 'contain' }}
        />
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src="https://skillgenie-college-uploads.s3.ap-south-1.amazonaws.com/TSG_Logo.png"
      alt="The Skill Genie"
      loading="lazy"
      onError={() => setImgError(true)}
      sx={{ height: size, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
    />
  );
};

const heroFeatures = [
  { Icon: AutoAwesomeRoundedIcon, title: 'AI-Powered Reviews', desc: 'Instant feedback on your code and architecture' },
  { Icon: PeopleAltRoundedIcon, title: 'Expert Mentorship', desc: 'Learn from seasoned industry professionals' },
  { Icon: RocketLaunchRoundedIcon, title: 'Build Real Products', desc: 'Ship portfolio-worthy projects with guidance' },
  { Icon: EmojiEventsRoundedIcon, title: 'Career Acceleration', desc: 'Land top roles with verified credentials' },
];

const NextGenRegister = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobileNumber: '',
    username: '',
    password: '',
    confirmPassword: '',
    referralId: searchParams.get('ref') || '',
  });
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Referral Validation State
  const [isValidatingReferral, setIsValidatingReferral] = useState(false);
  const [referralStatus, setReferralStatus] = useState(null); // { valid: boolean, name: string }

  const validateReferral = useCallback(async (code) => {
    if (!code || code.length < 3) {
      setReferralStatus(null);
      return;
    }

    setIsValidatingReferral(true);
    try {
      const sanitizedCode = code.trim().toUpperCase();
      const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/validate-referral/${sanitizedCode}`);
      // The endpoint answers
      //   { success, message, data: { isValid, referrer: { fullName, username } } }
      // but this read `res.data.valid` and `res.data.name`, one level too high.
      // Both were undefined, so a 200 carrying isValid:true fell to the else
      // branch and the field showed "Invalid Referral Code" for a code the
      // server had just confirmed — and the referrer name rendered as
      // undefined, which is the stray `undefined` request in the network log.
      //
      // Unwrap the envelope when present, and still accept a flat body so this
      // keeps working if the response is ever flattened.
      const body = res.data && typeof res.data === 'object' ? res.data : {};
      const detail = body.data && typeof body.data === 'object' ? body.data : body;
      const isValid = detail.isValid === true || detail.valid === true;
      const referrer = detail.referrer && typeof detail.referrer === 'object' ? detail.referrer : {};

      if (isValid) {
        setReferralStatus({
          valid: true,
          name: referrer.fullName || referrer.username || detail.name || '',
        });
      } else {
        setReferralStatus({ valid: false });
      }
    } catch (err) {
      console.error('Referral validation failed:', err);
      setReferralStatus({ valid: false });
    } finally {
      setIsValidatingReferral(false);
    }
  }, []);

  // Debounce referral validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.referralId) {
        validateReferral(formData.referralId);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.referralId, validateReferral]);

  // Initial validation if referralId is present from URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      validateReferral(ref);
    }
  }, [validateReferral, searchParams]);

  const handleChange = (e) => {
    let { name, value, type, checked } = e.target;

    if (name === 'mobileNumber') {
      // Allow only digits
      value = value.replace(/\D/g, '');
      // Limit to 10 digits
      if (value.length > 10) value = value.slice(0, 10);
    }

    setFormData((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const validateMobile = (num) => {
    // Exactly 10 digits, starts with 6, 7, 8, or 9
    return /^[6789]\d{9}$/.test(num);
  };

  const validatePassword = (pass) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(pass);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (formData.password !== formData.confirmPassword) return setError('Passwords do not match');
    if (!validatePassword(formData.password))
      return setError('Password must be 8+ chars with uppercase, lowercase, number & special character');
    if (!validateMobile(formData.mobileNumber)) {
      return setError('Enter a valid 10-digit mobile number starting with 6, 7, 8, or 9');
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/nextgen-builder/register`, formData);
      if (res.data.success) {
        setSuccess('Account created! Redirecting to sign in…');
        setTimeout(() => navigate('/nextgen-builder/login'), 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', fontFamily: '"Plus Jakarta Sans","Inter",system-ui,sans-serif' }}>

      {/* ── Left Hero Panel ─────────────────────────────────────────── */}
      <Box sx={{
        display: { xs: 'none', lg: 'flex' },
        width: { lg: '44%' },
        flexShrink: 0,
        background: 'linear-gradient(150deg, #3A18C8 0%, #6C3BFF 48%, #9061F9 100%)',
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: { lg: 5, xl: 7 },
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{ position:'absolute',top:-120,right:-120,width:360,height:360,borderRadius:'50%',
                    background:'rgba(255,255,255,0.06)',pointerEvents:'none' }} />
        <Box sx={{ position:'absolute',bottom:-80,left:-80,width:280,height:280,borderRadius:'50%',
                    background:'rgba(255,255,255,0.04)',pointerEvents:'none' }} />
        <Box sx={{ position:'absolute',top:'40%',right:'8%',width:160,height:160,borderRadius:'50%',
                    background:'rgba(255,255,255,0.03)',pointerEvents:'none' }} />
        <Box sx={{ position:'absolute',inset:0,
                    backgroundImage:'radial-gradient(rgba(255,255,255,0.06) 1px,transparent 1px)',
                    backgroundSize:'28px 28px',pointerEvents:'none' }} />

        <Box sx={{ position:'relative', zIndex:1 }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mb:5 }}>
            <TSGLogo size={40} onDark />
            <Typography sx={{ color:'rgba(255,255,255,0.92)', fontWeight:700, fontSize:'0.95rem' }}>
              The Skill Genie
            </Typography>
          </Box>

          <Chip label="TSG NEXUS PLATFORM" size="small" sx={{
            mb:2.5, bgcolor:'rgba(255,255,255,0.12)', color:'rgba(255,255,255,0.92)',
            border:'1px solid rgba(255,255,255,0.22)', fontWeight:700,
            fontSize:'0.6rem', letterSpacing:'0.1em', height:24,
          }} />

          <Typography variant="h3" sx={{
            color:'#FFF', fontWeight:800, letterSpacing:'-0.03em',
            lineHeight:1.12, mb:2.5, fontSize:{ lg:'1.85rem', xl:'2.1rem' },
          }}>
            Build the Next<br />Generation of<br />Products
          </Typography>

          <Typography sx={{
            color:'rgba(255,255,255,0.7)', fontSize:'0.92rem',
            lineHeight:1.75, mb:4.5, maxWidth:340,
          }}>
            Join thousands of ambitious builders shipping real products with AI-powered reviews and expert mentorship.
          </Typography>

          <Box sx={{ display:'flex', flexDirection:'column', gap:2.5 }}>
            {heroFeatures.map(({ Icon, title, desc }) => (
              <Box key={title} sx={{ display:'flex', alignItems:'flex-start', gap:1.75 }}>
                <Box sx={{
                  width:38, height:38, borderRadius:'11px', flexShrink:0,
                  bgcolor:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.1)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                }}>
                  <Icon sx={{ color:'#fff', fontSize:18 }} />
                </Box>
                <Box>
                  <Typography sx={{ color:'#fff', fontWeight:700, fontSize:'0.875rem', lineHeight:1.3 }}>
                    {title}
                  </Typography>
                  <Typography sx={{ color:'rgba(255,255,255,0.6)', fontSize:'0.78rem', lineHeight:1.55, mt:0.3 }}>
                    {desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

      </Box>

      {/* ── Right Form Panel ────────────────────────────────────────── */}
      <Box sx={{
        flex:1, bgcolor:'#F8FAFC',
        display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'flex-start',
        px:{ xs:2, sm:4, md:5 }, py:{ xs:4, sm:5 },
        overflowY:'auto', minHeight:'100vh',
      }}>
        {/* Mobile-only logo */}
        <Box sx={{ display:{ xs:'flex', lg:'none' }, flexDirection:'column',
                    alignItems:'center', mb:4, gap:1.5 }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
            <TSGLogo size={36} />
            <Typography sx={{ fontWeight:700, color:'#0F172A', fontSize:'0.95rem' }}>
              The Skill Genie
            </Typography>
          </Box>
          <Chip label="TSG NEXUS" size="small" sx={{
            bgcolor:'rgba(108,59,255,0.07)', color:'#6C3BFF',
            border:'1px solid rgba(108,59,255,0.18)', fontWeight:700,
            fontSize:'0.6rem', letterSpacing:'0.08em', height:22,
          }} />
        </Box>

        <motion.div
          initial={{ opacity:0, y:18 }}
          animate={{ opacity:1, y:0 }}
          transition={{ duration:0.35, ease:[0.25,0.1,0.25,1] }}
          style={{ width:'100%', maxWidth:520 }}
        >
          <Paper elevation={0} sx={{
            p:{ xs:3.5, sm:5 },
            borderRadius:'20px',
            border:'1px solid #E5E7EB',
            bgcolor:'#FFFFFF',
            boxShadow:'0 1px 3px rgba(0,0,0,0.06),0 4px 8px rgba(0,0,0,0.04),0 20px 48px -12px rgba(108,59,255,0.1)',
          }}>
            {/* Heading */}
            <Box sx={{ mb:3.5 }}>
              <Chip label="NEW ACCOUNT" size="small" sx={{
                mb:1.5, bgcolor:'rgba(108,59,255,0.07)', color:'#6C3BFF',
                border:'1px solid rgba(108,59,255,0.18)', fontWeight:700,
                fontSize:'0.6rem', letterSpacing:'0.1em', height:22,
              }} />
              <Typography variant="h5" sx={{ color:'#0F172A', fontWeight:800, mb:0.75 }}>
                Create your account
              </Typography>
              <Typography variant="body2" sx={{ color:'#64748B' }}>
                Build real products with AI reviews &amp; expert mentorship
              </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb:2.5 }}>{error}</Alert>}
            {success && (
              <Alert severity="success" icon={<CheckCircleRoundedIcon fontSize="small" />} sx={{ mb:2.5 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              {/* Full Name */}
              <TextField
                fullWidth label="Full Name" name="fullName" required
                value={formData.fullName} onChange={handleChange}
                sx={{ mb:2.5 }}
              />

              {/* Email + Mobile */}
              <Grid container spacing={2} sx={{ mb:2.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Email Address" name="email" type="email" required
                    value={formData.email} onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Mobile Number" name="mobileNumber" type="tel" required
                    value={formData.mobileNumber} onChange={handleChange}
                    inputProps={{
                      maxLength: 10,
                      inputMode: 'numeric',
                      autoComplete: 'tel',
                    }}
                    onWheel={(e) => e.target.blur()}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pastedData = e.clipboardData.getData('text');
                      const sanitized = pastedData.replace(/\D/g, '').slice(0, 10);
                      setFormData(prev => ({ ...prev, mobileNumber: sanitized }));
                    }}
                  />
                </Grid>
              </Grid>

              {/* Username */}
              <TextField
                fullWidth label="Username" name="username" required
                value={formData.username} onChange={handleChange}
                sx={{ mb:2.5 }}
              />

              {/* Password + Confirm */}
              <Grid container spacing={2} sx={{ mb:2.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Password" name="password" required
                    type={showPass ? 'text' : 'password'}
                    value={formData.password} onChange={handleChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPass((p) => !p)} edge="end" size="small"
                            sx={{ color:'#94A3B8', '&:hover':{ color:'#6C3BFF', bgcolor:'rgba(108,59,255,0.07)' } }}>
                            {showPass ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Confirm Password" name="confirmPassword" required
                    type={showConfirm ? 'text' : 'password'}
                    value={formData.confirmPassword} onChange={handleChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowConfirm((p) => !p)} edge="end" size="small"
                            sx={{ color:'#94A3B8', '&:hover':{ color:'#6C3BFF', bgcolor:'rgba(108,59,255,0.07)' } }}>
                            {showConfirm ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              {/* Password hint */}
              <Box sx={{
                mb:2.5, px:2, py:1.25, borderRadius:'10px',
                bgcolor:'#F8FAFC', border:'1px solid #E2E8F0',
              }}>
                <Typography variant="caption" sx={{ color:'#64748B', lineHeight:1.6 }}>
                  Password must be 8+ characters with uppercase, lowercase, a number, and a special character (@$!%*?&)
                </Typography>
              </Box>

              {/* Referral Code */}
              <TextField
                fullWidth label="Referral Code (Optional)" name="referralId"
                value={formData.referralId} onChange={handleChange}
                placeholder="e.g. NGBXXXXX"
                error={referralStatus?.valid === false}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CardGiftcardRoundedIcon sx={{ color:'#94A3B8', fontSize:19 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {isValidatingReferral && <CircularProgress size={16} />}
                      {!isValidatingReferral && referralStatus?.valid && (
                        <CheckCircleRoundedIcon sx={{ color: '#10B981', fontSize: 20 }} />
                      )}
                    </InputAdornment>
                  )
                }}
                sx={{ mb: referralStatus ? 0.5 : 2.5 }}
              />

              {referralStatus && (
                <Box sx={{ mb: 2, px: 1 }}>
                  {referralStatus.valid ? (
                    <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      ✓ Referral Applied. Referred by {referralStatus.name}
                    </Typography>
                  ) : (
                    <Typography variant="caption" sx={{ color: '#EF4444', fontWeight: 600 }}>
                      Invalid Referral Code
                    </Typography>
                  )}
                </Box>
              )}

              {/* Submit */}
              <Button
                type="submit" fullWidth size="large" disabled={loading}
                sx={{
                  py:1.65, borderRadius:'12px', fontWeight:700, fontSize:'0.95rem',
                  letterSpacing:'-0.01em', color:'#fff',
                  background:'linear-gradient(135deg, #6C3BFF 0%, #8B5CF6 100%)',
                  boxShadow:'0 4px 14px rgba(108,59,255,0.32)',
                  '&:hover': {
                    background:'linear-gradient(135deg, #5020E0 0%, #7C3AED 100%)',
                    boxShadow:'0 8px 28px rgba(108,59,255,0.44)',
                    transform:'translateY(-1px)',
                  },
                  '&:disabled':{ opacity:0.6, boxShadow:'none', transform:'none' },
                }}
              >
                {loading ? <CircularProgress size={20} sx={{ color:'#fff' }} /> : 'Create Account'}
              </Button>
            </Box>

            <Divider sx={{ my:3 }} />

            <Box sx={{ textAlign:'center' }}>
              <Typography variant="body2" sx={{ color:'#64748B' }}>
                Already have an account?{' '}
                <Link to="/nextgen-builder/login"
                  style={{ color:'#6C3BFF', fontWeight:700, textDecoration:'none' }}>
                  Sign In
                </Link>
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Box>
    </Box>
  );
};

export default NextGenRegister;
